import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  BarChart3,
  Users,
  Car,
  Calendar,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Loader2,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminReports = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [vehicles, setVehicles] = useState(null);
  const [trends, setTrends] = useState(null);
  const [services, setServices] = useState(null);
  
  // Date filter for revenue
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const { toast } = useToast();
  const { token } = useAuth();
  
  const headers = { Authorization: `Bearer ${token}` };
  
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reports/dashboard`, { headers });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast({ title: 'Feil', description: 'Kunne ikke hente dashboard-data', variant: 'destructive' });
    }
  }, [token, toast]);
  
  const fetchRevenue = useCallback(async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await axios.get(`${API}/reports/revenue`, { headers, params });
      setRevenue(response.data);
    } catch (error) {
      console.error('Error fetching revenue:', error);
    }
  }, [token, startDate, endDate]);
  
  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reports/customers`, { headers });
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, [token]);
  
  const fetchVehicles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reports/vehicles`, { headers });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  }, [token]);
  
  const fetchTrends = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reports/bookings/trends`, { headers, params: { days: 30 } });
      setTrends(response.data);
    } catch (error) {
      console.error('Error fetching trends:', error);
    }
  }, [token]);
  
  const fetchServices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reports/services/popularity`, { headers });
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  }, [token]);
  
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchDashboard(),
      fetchRevenue(),
      fetchCustomers(),
      fetchVehicles(),
      fetchTrends(),
      fetchServices()
    ]);
    setLoading(false);
  }, [fetchDashboard, fetchRevenue, fetchCustomers, fetchVehicles, fetchTrends, fetchServices]);
  
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast({ title: 'Oppdatert', description: 'Rapportdata er oppdatert' });
  };
  
  // Stat Card Component
  const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = 'blue', subtext }) => (
    <Card className="bg-gray-800 border-gray-700">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-gray-400 text-sm">{title}</p>
            <p className="text-3xl font-bold text-white mt-1">{value}</p>
            {subtext && <p className="text-gray-500 text-sm mt-1">{subtext}</p>}
          </div>
          <div className={`p-3 rounded-xl bg-${color}-600/20`}>
            <Icon className={`w-6 h-6 text-${color}-400`} />
          </div>
        </div>
        {trend !== undefined && (
          <div className="flex items-center mt-3">
            {trend === 'up' ? (
              <TrendingUp className="w-4 h-4 text-green-400 mr-1" />
            ) : trend === 'down' ? (
              <TrendingDown className="w-4 h-4 text-red-400 mr-1" />
            ) : null}
            <span className={`text-sm ${trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-gray-400'}`}>
              {trendValue}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
  
  // Progress Bar Component
  const ProgressBar = ({ label, value, max, color = 'blue' }) => {
    const percentage = max > 0 ? (value / max) * 100 : 0;
    return (
      <div className="space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-gray-300">{label}</span>
          <span className="text-gray-400">{value}</span>
        </div>
        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
          <div 
            className={`h-full bg-${color}-500 rounded-full transition-all duration-500`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
        <span className="ml-3 text-gray-400">Laster rapporter...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="admin-reports">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-yellow-500" />
            Rapporter & Analyse
          </h1>
          <p className="text-gray-400 mt-1">Forretningsmetrikker og innsikt</p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          className="bg-yellow-600 hover:bg-yellow-700"
        >
          {refreshing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Oppdater
        </Button>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-800">
          <TabsTrigger value="overview" className="data-[state=active]:bg-yellow-600">
            <BarChart3 className="w-4 h-4 mr-2" />
            Oversikt
          </TabsTrigger>
          <TabsTrigger value="customers" className="data-[state=active]:bg-yellow-600">
            <Users className="w-4 h-4 mr-2" />
            Kunder
          </TabsTrigger>
          <TabsTrigger value="vehicles" className="data-[state=active]:bg-yellow-600">
            <Car className="w-4 h-4 mr-2" />
            Kjøretøy
          </TabsTrigger>
          <TabsTrigger value="bookings" className="data-[state=active]:bg-yellow-600">
            <Calendar className="w-4 h-4 mr-2" />
            Bookinger
          </TabsTrigger>
        </TabsList>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Quick Stats */}
          {dashboard && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  title="Bookinger i dag"
                  value={dashboard.quick_stats.todays_bookings}
                  icon={Calendar}
                  color="blue"
                />
                <StatCard
                  title="Ventende bookinger"
                  value={dashboard.quick_stats.pending_bookings}
                  icon={Clock}
                  color="yellow"
                />
                <StatCard
                  title="Denne måneden"
                  value={dashboard.quick_stats.bookings_this_month}
                  icon={TrendingUp}
                  trend={dashboard.quick_stats.monthly_growth_percent >= 0 ? 'up' : 'down'}
                  trendValue={`${dashboard.quick_stats.monthly_growth_percent > 0 ? '+' : ''}${dashboard.quick_stats.monthly_growth_percent}% fra forrige måned`}
                  color="green"
                />
                <StatCard
                  title="Konverteringsrate"
                  value={`${dashboard.conversion_rate}%`}
                  icon={CheckCircle}
                  subtext="Fullførte bookinger"
                  color="purple"
                />
              </div>
              
              {/* Totals */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 border-blue-700/50">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-300 text-sm">Totalt kunder</p>
                        <p className="text-4xl font-bold text-white">{dashboard.totals.total_customers}</p>
                      </div>
                      <Users className="w-12 h-12 text-blue-400 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-900/50 to-green-800/30 border-green-700/50">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-300 text-sm">Totalt kjøretøy</p>
                        <p className="text-4xl font-bold text-white">{dashboard.totals.total_vehicles}</p>
                      </div>
                      <Car className="w-12 h-12 text-green-400 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 border-purple-700/50">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-300 text-sm">Fullførte bookinger</p>
                        <p className="text-4xl font-bold text-white">{dashboard.totals.completed_bookings}</p>
                        <p className="text-purple-400 text-sm">av {dashboard.totals.total_bookings} totalt</p>
                      </div>
                      <CheckCircle className="w-12 h-12 text-purple-400 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
          
          {/* Booking Status Breakdown */}
          {revenue && (
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Booking-status fordeling</CardTitle>
                <CardDescription className="text-gray-400">
                  Oversikt over alle bookinger etter status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {Object.entries(revenue.status_breakdown).map(([status, count]) => {
                    const statusConfig = {
                      pending: { label: 'Venter', color: 'yellow', icon: Clock },
                      confirmed: { label: 'Bekreftet', color: 'blue', icon: CheckCircle },
                      completed: { label: 'Fullført', color: 'green', icon: CheckCircle },
                      cancelled: { label: 'Kansellert', color: 'red', icon: XCircle },
                      no_show: { label: 'Ikke møtt', color: 'gray', icon: AlertCircle }
                    };
                    const config = statusConfig[status] || { label: status, color: 'gray', icon: FileText };
                    const Icon = config.icon;
                    
                    return (
                      <div 
                        key={status}
                        className={`p-4 rounded-lg bg-${config.color}-900/30 border border-${config.color}-700/50`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className={`w-4 h-4 text-${config.color}-400`} />
                          <span className="text-gray-300 text-sm">{config.label}</span>
                        </div>
                        <p className="text-2xl font-bold text-white">{count}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* Customers Tab */}
        <TabsContent value="customers" className="space-y-6">
          {customers && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard
                  title="Totalt kunder"
                  value={customers.total_customers}
                  icon={Users}
                  color="blue"
                />
                <StatCard
                  title="Nye denne måneden"
                  value={customers.new_this_month}
                  icon={TrendingUp}
                  color="green"
                />
                <StatCard
                  title="Med kjøretøy"
                  value={customers.customers_with_vehicles}
                  icon={Car}
                  color="purple"
                />
                <StatCard
                  title="Bedriftskunder"
                  value={customers.customer_types?.business || 0}
                  icon={DollarSign}
                  color="yellow"
                />
              </div>
              
              {/* Customer Types */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Kundetyper</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(customers.customer_types || {}).map(([type, count]) => (
                    <ProgressBar
                      key={type}
                      label={type === 'private' ? 'Privat' : type === 'business' ? 'Bedrift' : type}
                      value={count}
                      max={customers.total_customers}
                      color={type === 'business' ? 'yellow' : 'blue'}
                    />
                  ))}
                </CardContent>
              </Card>
              
              {/* Top Customers */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Topp 10 kunder</CardTitle>
                  <CardDescription className="text-gray-400">
                    Kunder med flest bookinger
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {customers.top_customers?.map((customer, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-900 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            index < 3 ? 'bg-yellow-600' : 'bg-gray-700'
                          }`}>
                            <span className="text-sm font-bold text-white">{index + 1}</span>
                          </div>
                          <div>
                            <p className="text-white font-medium">{customer.name}</p>
                            <p className="text-gray-400 text-sm">{customer.email}</p>
                          </div>
                        </div>
                        <Badge className="bg-blue-600">
                          {customer.booking_count} bookinger
                        </Badge>
                      </div>
                    ))}
                    {(!customers.top_customers || customers.top_customers.length === 0) && (
                      <p className="text-gray-500 text-center py-4">Ingen kundedata tilgjengelig</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
        
        {/* Vehicles Tab */}
        <TabsContent value="vehicles" className="space-y-6">
          {vehicles && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard
                  title="Totalt kjøretøy"
                  value={vehicles.total_vehicles}
                  icon={Car}
                  color="blue"
                />
                <StatCard
                  title="EU-kontroll snart"
                  value={vehicles.upcoming_eu_control}
                  icon={AlertCircle}
                  subtext="Neste 30 dager"
                  color="yellow"
                />
                <StatCard
                  title="Forfalt EU-kontroll"
                  value={vehicles.overdue_eu_control}
                  icon={XCircle}
                  color="red"
                />
                <StatCard
                  title="Kjøretøytyper"
                  value={Object.keys(vehicles.vehicle_types || {}).length}
                  icon={FileText}
                  color="purple"
                />
              </div>
              
              {/* Vehicle Types */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Kjøretøytyper</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(vehicles.vehicle_types || {}).map(([type, count]) => (
                    <ProgressBar
                      key={type}
                      label={type}
                      value={count}
                      max={vehicles.total_vehicles}
                      color="green"
                    />
                  ))}
                </CardContent>
              </Card>
              
              {/* Top Brands */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Populære merker</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {Object.entries(vehicles.top_brands || {}).map(([brand, count]) => (
                      <div 
                        key={brand}
                        className="p-3 bg-gray-900 rounded-lg text-center"
                      >
                        <p className="text-white font-medium">{brand}</p>
                        <p className="text-gray-400 text-sm">{count} stk</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
        
        {/* Bookings Tab */}
        <TabsContent value="bookings" className="space-y-6">
          {trends && (
            <>
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Booking-trender (siste 30 dager)</CardTitle>
                  <CardDescription className="text-gray-400">
                    Totalt {trends.total_in_period} bookinger i perioden
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Simple bar chart using divs */}
                  <div className="space-y-2">
                    {trends.daily_bookings?.slice(-14).map((day) => {
                      const maxCount = Math.max(...trends.daily_bookings.map(d => d.count), 1);
                      const percentage = (day.count / maxCount) * 100;
                      return (
                        <div key={day.date} className="flex items-center gap-3">
                          <span className="text-gray-400 text-sm w-24">{day.date.substring(5)}</span>
                          <div className="flex-1 h-6 bg-gray-700 rounded overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-yellow-600 to-yellow-500 rounded transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-white font-medium w-8 text-right">{day.count}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              
              {/* Day of Week */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white">Bookinger per ukedag</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {Object.entries(trends.bookings_by_day_of_week || {}).map(([day, count]) => {
                      const maxCount = Math.max(...Object.values(trends.bookings_by_day_of_week || {}), 1);
                      return (
                        <ProgressBar
                          key={day}
                          label={day}
                          value={count}
                          max={maxCount}
                          color="yellow"
                        />
                      );
                    })}
                  </CardContent>
                </Card>
                
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white">Populære tidspunkter</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {Object.entries(trends.popular_time_slots || {}).slice(0, 8).map(([slot, count]) => {
                      const maxCount = Math.max(...Object.values(trends.popular_time_slots || {}), 1);
                      return (
                        <ProgressBar
                          key={slot}
                          label={slot}
                          value={count}
                          max={maxCount}
                          color="blue"
                        />
                      );
                    })}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminReports;
